<template>
  <b-nav-item-dropdown
    ref="notificationDropdown"
    class="dropdown-notification poda-notification-dropdown"
    right
    menu-class="poda-notification-menu shadow"
    toggle-class="poda-notification-toggle"
    @show="handleDropdownShow"
  >
    <template #button-content>
      <feather-icon
        icon="BellIcon"
        size="21"
        class="poda-bell-icon text-body"
        :badge="bellBadge"
        badge-classes="bg-danger poda-notify-badge"
      />
    </template>

    <div class="poda-notification-header">
      <div>
        <div class="poda-notification-header__title">
          Notifications
        </div>
        <div class="poda-notification-header__meta">
          {{ unreadCount }} unread
        </div>
      </div>
      <button
        type="button"
        class="poda-notification-header__action"
        :disabled="!unreadCount || isMarkingAll"
        @click.stop="markAllRead"
      >
        {{ isMarkingAll ? 'Updating...' : 'Mark all as read' }}
      </button>
    </div>

    <div
      v-if="isLoading"
      class="poda-notification-state"
    >
      <b-spinner
        small
        class="mr-50"
      />
      <span>Loading notifications...</span>
    </div>

    <div
      v-else-if="errorMessage"
      class="poda-notification-state poda-notification-state--error"
    >
      <div>{{ errorMessage }}</div>
      <button
        type="button"
        class="poda-notification-state__button"
        @click.stop="fetchNotifications"
      >
        Retry
      </button>
    </div>

    <div
      v-else-if="!visibleNotifications.length"
      class="poda-notification-state"
    >
      No notifications yet.
    </div>

    <div
      v-else
      class="poda-notification-list"
    >
      <div
        v-for="n in visibleNotifications"
        :key="n.id"
        class="poda-notification-row"
        :class="{ 'is-read': n.isRead }"
        @click="onNotificationClick(n)"
      >
        <div class="poda-notification-row__left">
          <b-avatar
            v-if="n.avatar"
            :src="n.avatar"
            size="40"
            variant="light-secondary"
            class="poda-notification-avatar"
          />
          <b-avatar
            v-else-if="n.sourceName !== 'System'"
            :text="n.initials"
            size="40"
            variant="light-secondary"
            class="poda-notification-avatar poda-notification-avatar--text"
          />
          <b-avatar
            v-else
            size="40"
            class="poda-notification-avatar poda-notification-avatar--icon poda-avatar--icon-bubble"
          >
            <feather-icon
              :icon="n.icon"
              size="18"
            />
          </b-avatar>
        </div>
        <div class="poda-notification-row__body">
          <div class="poda-notification-row__title">
            {{ n.title }}
          </div>
          <div class="poda-notification-row__message">
            {{ n.message }}
          </div>
          <div class="poda-notification-row__time">
            {{ n.timestamp }}
          </div>
        </div>
        <div
          class="poda-notification-row__right"
          @click.stop
        >
          <b-spinner
            v-if="activeReadId === n.id"
            small
          />
          <span
            v-else-if="!n.isRead"
            class="poda-notification-row__dot"
            title="Unread"
          />
        </div>
      </div>
    </div>

    <div class="poda-notification-footer">
      <b-link
        class="poda-notification-footer__link"
        @click="viewAll"
      >
        View all notifications
      </b-link>
    </div>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BLink, BAvatar, BSpinner,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import {
  VENDOR_NOTIFICATIONS_UPDATED_EVENT,
  emitVendorNotificationsUpdated,
  parseVendorNotificationsResponse,
  resolveVendorNotificationRoute,
} from '@/utils/vendorNotifications'

export default {
  name: 'NotificationDrop',
  components: {
    BNavItemDropdown,
    BLink,
    BAvatar,
    BSpinner,
  },
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      isMarkingAll: false,
      activeReadId: null,
      errorMessage: '',
    }
  },
  computed: {
    visibleNotifications() {
      return this.notifications
    },
    bellBadge() {
      if (!this.unreadCount) return null
      return this.unreadCount > 99 ? '99+' : this.unreadCount
    },
  },
  created() {
    this.handleNotificationsUpdated = event => {
      const unreadCount = Number(event?.detail?.unreadCount)
      if (!Number.isNaN(unreadCount)) {
        this.unreadCount = unreadCount
      } else {
        this.fetchUnreadCount()
      }

      if (event?.detail?.refreshList) {
        this.fetchNotifications()
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener(VENDOR_NOTIFICATIONS_UPDATED_EVENT, this.handleNotificationsUpdated)
    }
  },
  mounted() {
    this.fetchUnreadCount()
    this._notificationPollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.fetchUnreadCount()
      }
    }, 60000)
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(VENDOR_NOTIFICATIONS_UPDATED_EVENT, this.handleNotificationsUpdated)
    }
    if (this._notificationPollTimer) {
      clearInterval(this._notificationPollTimer)
      this._notificationPollTimer = null
    }
  },
  methods: {
    async handleDropdownShow() {
      await Promise.all([
        this.fetchNotifications(),
        this.fetchUnreadCount(),
      ])
    },
    async fetchUnreadCount() {
      try {
        const response = await vendorPanelApi.notificationsUnreadCount()
        this.unreadCount = Number(response?.data?.data?.unread_count || 0)
      } catch (error) {
        this.unreadCount = 0
      }
    },
    async fetchNotifications() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await vendorPanelApi.notifications({
          page: 1,
          per_page: 5,
        })
        const { notifications, unreadCount } = parseVendorNotificationsResponse(response)

        this.notifications = notifications
        this.unreadCount = unreadCount
      } catch (error) {
        this.notifications = []
        this.errorMessage = error?.response?.data?.message || 'Unable to load notifications.'
      } finally {
        this.isLoading = false
      }
    },
    async onNotificationClick(item) {
      if (!item || this.activeReadId === item.id) return

      this.activeReadId = item.id

      try {
        if (!item.isRead) {
          const response = await vendorPanelApi.notificationRead(item.id)
          const unreadCount = Number(response?.data?.data?.unread_count || Math.max(this.unreadCount - 1, 0))

          item.isRead = true
          this.unreadCount = unreadCount
          emitVendorNotificationsUpdated({ unreadCount })
        }

        const route = resolveVendorNotificationRoute(item)
        if (route) {
          if (this.$refs.notificationDropdown) {
            this.$refs.notificationDropdown.hide()
          }
          this.$router.push(route).catch(() => {})
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to update this notification.'
      } finally {
        this.activeReadId = null
      }
    },
    async markAllRead() {
      if (!this.unreadCount || this.isMarkingAll) return

      this.isMarkingAll = true

      try {
        const response = await vendorPanelApi.notificationsMarkAllRead()
        const unreadCount = Number(response?.data?.data?.unread_count || 0)

        this.notifications = this.notifications.map(item => ({
          ...item,
          isRead: true,
        }))
        this.unreadCount = unreadCount
        emitVendorNotificationsUpdated({ unreadCount, refreshList: true })
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || 'Unable to mark all notifications as read.'
      } finally {
        this.isMarkingAll = false
      }
    },
    viewAll() {
      if (this.$refs.notificationDropdown) {
        this.$refs.notificationDropdown.hide()
      }
      if (this.$route.name !== 'notification') {
        this.$router.push({ name: 'notification' }).catch(() => {})
      }
    },
  },
}
</script>

<style lang="scss">
.poda-notification-dropdown {
  .poda-notification-toggle {
    padding: 0.25rem 0.35rem;
  }

  .poda-bell-icon {
    stroke-width: 2;
  }

  .poda-notify-badge {
    min-width: 1.1rem;
    min-height: 1.1rem;
    line-height: 1;
    font-size: 0.65rem;
    font-weight: 700;
  }
}

.poda-notification-menu {
  min-width: 22rem;
  max-width: 24rem;
  padding: 0;
  border: none;
  border-radius: 0.65rem;
  box-shadow: 0 8px 28px rgba(24, 22, 45, 0.12) !important;
  overflow: hidden;
}

.poda-notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #efeff4;
  background: #fff;
}

.poda-notification-header__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d2a3a;
}

.poda-notification-header__meta {
  font-size: 0.75rem;
  color: #8a869d;
}

.poda-notification-header__action,
.poda-notification-state__button {
  border: 0;
  background: transparent;
  color: #5c4b7a;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0;
}

.poda-notification-header__action[disabled] {
  color: #b6b1c7;
  cursor: not-allowed;
}

.poda-notification-list {
  max-height: 20rem;
  overflow-y: auto;
}

.poda-notification-state {
  min-height: 7rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #8a869d;
  padding: 1rem;
  text-align: center;
}

.poda-notification-state--error {
  flex-direction: column;
}

.poda-notification-row {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #efeff4;
  cursor: pointer;
  transition: background 0.12s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: #faf9fc;
  }
}

.poda-notification-row.is-read {
  background: #fbfbfd;
}

.poda-avatar--icon-bubble {
  background: #e8e4f5 !important;

  .feather,
  svg {
    color: #5c4b7a;
  }
}

.poda-notification-avatar--text {
  .b-avatar-text {
    font-size: 0.85rem;
    font-weight: 700;
    color: #4a4a5c;
  }
}

.poda-notification-row__body {
  flex: 1;
  min-width: 0;
}

.poda-notification-row__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #2d2a3a;
  line-height: 1.25;
}

.poda-notification-row__message {
  font-size: 0.8rem;
  color: #8a869d;
  line-height: 1.35;
  margin-top: 0.1rem;
}

.poda-notification-row__time {
  font-size: 0.7rem;
  color: #b0abbf;
  margin-top: 0.35rem;
}

.poda-notification-row__right {
  flex-shrink: 0;
  width: 1.25rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0.2rem;
}

.poda-notification-row__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1a1a24;
}

.poda-notification-footer {
  border-top: 1px solid #efeff4;
  padding: 0.75rem 1rem;
  text-align: center;
  background: #fff;
}

.poda-notification-footer__link {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6e6a82 !important;

  &:hover {
    color: #2d2a3a !important;
  }
}
</style>
